import { DataLayerAction } from '@corratech/pylot-tag-manager'
import { getInitScriptContent } from '@corratech/pylot-gtm/src/utils'
import { useStoreConfig } from 'config'
import { getApplyMapping } from 'stores/ooka_uae_store/components/OOKA/pylot-gtm/src/mapping/getApplyMapping'
import {
    GTMActionOverride,
    GTMEventDataMapping
} from '@corratech/pylot-gtm/src/GTM.d'
import { useDataLayerData } from '@corratech/pylot-gtm/src/hooks/useDataLayerData'
import { useEventQueue } from '@components//OOKA/pylot-gtm/src/hooks/useEventQueue'
import '@corratech/pylot-gtm/src/utils/helper/data-layer-helper'
import { getEventDataConsumer } from 'stores/ooka_uae_store/components/OOKA/pylot-gtm/src/utils/getEventDataConsumer'
import { useRouter } from 'next/router'

declare global {
    interface Window {
        dataLayer?: Array<unknown>
    }
}

type GTMData = {
    gtmHandler: DataLayerAction
    gtmInitScript: string
    gtmNoScript: string
}

export const useGTM = (
    mappingOverride: GTMEventDataMapping = {},
    actionOverride: GTMActionOverride = {},
    eventQueueBeforeRules = {},
    eventQueueAfterRules = {},
    delayToNextPageEvents = []
): GTMData => {
    const {
        seo: {
            tagManager: { gtm }
        }
    } = useStoreConfig()
    const isEnabled = gtm && gtm.enabled
    const ID = isEnabled && gtm?.containerId ? gtm.containerId : ''
    const { initScript, noScript } = getInitScriptContent(ID)

    const eventQueue = useEventQueue(
        eventQueueBeforeRules,
        eventQueueAfterRules,
        delayToNextPageEvents
    )
    const { getStoredData, addStoredData, resetStoredData } = useDataLayerData()
    const router = useRouter()
    const consumeEventData = getEventDataConsumer(
        eventQueue,
        getStoredData,
        addStoredData,
        resetStoredData,
        router
    )

    const applyMapping = getApplyMapping(mappingOverride)
    const handleDataLayerAction: DataLayerAction = (action) => {
        if (!isEnabled) {
            return
        }
        let mappedData = applyMapping(action)
        if (action.type && typeof actionOverride[action.type] === 'function') {
            // Data, processed by an action override does not need 'type'
            mappedData = {
                type: '',
                data: actionOverride[action.type](
                    mappedData.data,
                    addStoredData
                )
            }
        }
        consumeEventData(mappedData)
    }

    return {
        gtmHandler: handleDataLayerAction,
        gtmInitScript: isEnabled ? initScript : '',
        gtmNoScript: isEnabled ? noScript : ''
    }
}
